import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, Eye, EyeOff, MessageCircle, Pin, PinOff, TrendingUp, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const StarRow = ({ rating }) => (
  <span className="inline-flex gap-0.5" aria-label={`${rating} stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={12} className={n <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-slate-700"} />
    ))}
  </span>
);

export const AdminReviewsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [sentiment, setSentiment] = useState(null);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/reviews`, authHeader()),
        axios.get(`${API}/admin/reviews/sentiment?days=30`, authHeader()),
      ]);
      setData(r.data);
      setSentiment(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePublic = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/reviews/${id}/toggle-public`, {}, authHeader());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    } finally {
      setBusy(null);
    }
  };

  const toggleFeatured = async (id) => {
    setBusy(id);
    try {
      const res = await axios.post(`${API}/admin/reviews/${id}/toggle-featured`, {}, authHeader());
      toast.success(res.data.is_featured ? "Pinned to the top of the landing strip" : "Un-pinned");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not pin");
    } finally {
      setBusy(null);
    }
  };

  const restore = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/reviews/${id}/restore`, {}, authHeader());
      toast.success("Back on the landing page, click count reset");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore");
    } finally {
      setBusy(null);
    }
  };

  const runSweep = async () => {
    setBusy("sweep");
    try {
      const { data: result } = await axios.post(`${API}/admin/reviews/performance/run`, {}, authHeader());
      toast.success(
        `Reviewed ${result.reviewed} — ${result.retired} retired, ${result.thanked} reviewer${
          result.thanked === 1 ? "" : "s"
        } thanked`
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sweep failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!data) return null;
  const { reviews, totals } = data;

  return (
    <div className="space-y-6" data-testid="admin-reviews-tab">
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { label: "Reviews", value: totals.count },
          { label: "Avg rating", value: totals.avg_rating ? `${totals.avg_rating} ★` : "—" },
          { label: "Avg NPS", value: totals.avg_nps ?? "—" },
          { label: "Published", value: totals.public_count },
        ].map((s) => (
          <div key={s.label} className="border border-slate-800 bg-slate-900/50 p-5">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">{s.label}</p>
            <p className="text-[#D4AF37] font-serif text-2xl">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-slate-800 bg-slate-900/50 p-5 md:p-6" data-testid="review-revenue-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <p className="text-slate-300 text-sm">What the testimonials bring in</p>
          <button
            onClick={runSweep}
            disabled={busy === "sweep"}
            data-testid="review-sweep-btn"
            className="inline-flex items-center gap-2 border border-slate-700 px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
          >
            {busy === "sweep" ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
            Run performance sweep
          </button>
        </div>
        <p className="text-slate-500 text-xs mb-5 max-w-2xl">
          A click on a testimonial is credited with any session booked in the next 30 days. A quote with 25+ clicks
          and no booking after three weeks on the page is retired automatically — you can always restore it.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { label: "Testimonial clicks", value: totals.attributed_clicks ?? 0, testid: "review-total-clicks" },
            { label: "Sessions paid after a click", value: totals.attributed_paid ?? 0, testid: "review-total-paid" },
            {
              label: "Revenue credited",
              value: `€${(totals.attributed_revenue_eur ?? 0).toLocaleString()}`,
              testid: "review-total-revenue",
            },
          ].map((s) => (
            <div key={s.label} className="border border-slate-800 bg-slate-950/60 p-4" data-testid={s.testid}>
              <p className="text-[#D4AF37] font-serif text-2xl">{s.value}</p>
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {sentiment && sentiment.series && (
        <div className="border border-slate-800 bg-slate-900/50 p-5 md:p-6" data-testid="sentiment-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-slate-300 text-sm">30-day sentiment</p>
          </div>
          {(() => {
            const s = sentiment.series;
            const max = 5;
            const width = 100;
            const step = s.length > 1 ? width / (s.length - 1) : 0;
            const pts = s
              .map((p, i) => (p.avg_rating == null ? null : `${(i * step).toFixed(2)},${(40 - (p.avg_rating / max) * 40).toFixed(2)}`))
              .filter(Boolean)
              .join(" ");
            const totalReviews = s.reduce((sum, p) => sum + p.count, 0);
            return (
              <>
                <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-24 mb-2">
                  <line x1="0" y1="20" x2="100" y2="20" stroke="#1F2937" strokeWidth="0.2" />
                  {pts && <polyline points={pts} fill="none" stroke="#D4AF37" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />}
                  {s.map((p, i) =>
                    p.avg_rating == null ? null : (
                      <circle key={i} cx={i * step} cy={40 - (p.avg_rating / max) * 40} r="0.6" fill="#D4AF37" vectorEffect="non-scaling-stroke" />
                    )
                  )}
                </svg>
                <div className="flex justify-between text-[10px] text-slate-500">
                  <span>{s[0]?.date}</span>
                  <span data-testid="sentiment-total">{totalReviews} reviews · scale 1–5</span>
                  <span>{s[s.length - 1]?.date}</span>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {reviews.length === 0 ? (
        <div className="border border-dashed border-slate-800 p-12 text-center text-slate-500 text-sm">
          <MessageCircle className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          No reviews yet. Every completed session auto-triggers a review request email.
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div
              key={r.review_id}
              className={`border p-5 md:p-6 ${r.is_public ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.03]" : "border-slate-800 bg-slate-900/50"}`}
              data-testid={`review-row-${r.review_id}`}
            >
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <StarRow rating={r.rating} />
                {r.is_featured && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#D4AF37] text-slate-950 font-semibold">
                    📌 Pinned{r.auto_featured_since ? " · auto" : ""}
                    {typeof r.featured_click_count === "number" && r.featured_click_count > 0 && (
                      <span data-testid={`review-clicks-${r.review_id}`} className="ml-1.5">
                        · {r.featured_click_count} click{r.featured_click_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </span>
                )}
                {r.nps !== null && r.nps !== undefined && (
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                      r.nps >= 9 ? "bg-green-500/20 text-green-400" : r.nps >= 7 ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-red-500/15 text-red-400"
                    }`}
                  >
                    NPS {r.nps}
                  </span>
                )}
                <span className="text-slate-400 text-xs">{r.client_name}</span>
                <span className="text-slate-600 text-xs">·</span>
                <span className="text-slate-500 text-xs capitalize">{r.session_type} · {(r.created_at || "").slice(0, 10)}</span>
                {r.client_public_ok && !r.is_public && !r.retired_at && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#D4AF37]/40 text-[#D4AF37]">
                    Client OK'd
                  </span>
                )}
                {r.retired_at && (
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-red-500/15 text-red-400"
                    data-testid={`review-retired-${r.review_id}`}
                  >
                    Retired{r.retired_automatically ? " · auto" : ""}
                  </span>
                )}
              </div>
              {r.retired_reason && (
                <p className="text-red-300/70 text-xs mb-3" data-testid={`review-retired-reason-${r.review_id}`}>
                  {r.retired_reason}
                </p>
              )}
              {r.comment && (
                <p className="text-slate-300 text-sm leading-relaxed mb-4 italic">"{r.comment}"</p>
              )}
              {(r.attributed_bookings > 0 || (r.featured_click_count || 0) > 0) && (
                <p className="text-xs text-slate-500 mb-4" data-testid={`review-revenue-${r.review_id}`}>
                  {r.featured_click_count || 0} click{(r.featured_click_count || 0) === 1 ? "" : "s"}
                  <span className="mx-2 text-slate-700">→</span>
                  {r.attributed_bookings || 0} booking{r.attributed_bookings === 1 ? "" : "s"}
                  <span className="mx-2 text-slate-700">→</span>
                  <span className="text-[#D4AF37]">
                    €{(r.attributed_revenue_eur || 0).toLocaleString()} from {r.attributed_paid || 0} paid
                  </span>
                </p>
              )}
              <div className="flex justify-end gap-2">
                {r.retired_at && (
                  <button
                    onClick={() => restore(r.review_id)}
                    disabled={busy === r.review_id}
                    data-testid={`review-restore-${r.review_id}`}
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-50 transition-colors"
                  >
                    {busy === r.review_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Restore
                  </button>
                )}
                {r.thank_you_sent_at && (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest text-emerald-400"
                    data-testid={`review-thanked-${r.review_id}`}
                  >
                    Reviewer thanked
                  </span>
                )}
                <button
                  onClick={() => toggleFeatured(r.review_id)}
                  disabled={busy === r.review_id}
                  data-testid={`review-pin-${r.review_id}`}
                  className={`inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-50 transition-colors ${
                    r.is_featured
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-slate-700 text-slate-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  {r.is_featured ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                  {r.is_featured ? "Unpin" : "Pin"}
                </button>
                <button
                  onClick={() => togglePublic(r.review_id)}
                  disabled={busy === r.review_id}
                  data-testid={`review-toggle-${r.review_id}`}
                  className={`inline-flex items-center gap-2 border px-4 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-50 transition-colors ${
                    r.is_public
                      ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      : "border-slate-700 text-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  {busy === r.review_id ? <Loader2 className="w-3 h-3 animate-spin" /> : r.is_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {r.is_public ? "Published on landing" : "Publish on landing"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
